import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  Avatar,
  Badge,
  CircularProgress,
  Popover,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Close,
  Send,
  Minimize,
  AddReactionOutlined
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { chatService } from '../../services/chatService';

const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '🚀', '💯'];

const ChatWindow = ({ 
  currentUser, 
  recipient, 
  onClose, 
  onMinimize, 
  position,
  unread = 0,
  onRead
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [activeMsgForReaction, setActiveMsgForReaction] = useState(null);

  const messagesEndRef = useRef(null);
  const subscriptionRef = useRef(null);
  const broadcastChannelRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleOpenReactionPicker = (event, msg) => {
    event.stopPropagation();
    setPopoverAnchor(event.currentTarget);
    setActiveMsgForReaction(msg);
  };

  const handleCloseReactionPicker = () => {
    setPopoverAnchor(null);
    setActiveMsgForReaction(null);
  };

  const handleToggleReaction = async (msg, emoji) => {
    if (!msg || !currentUser) return;
    const currentReactions = msg.reactions || {};
    const myId = String(currentUser.userid);
    const hadThisEmoji = (currentReactions[emoji] || []).includes(myId);

    // Enforce 1 reaction per user per message: strip user ID from all existing emoji lists
    const updatedReactions = {};
    Object.entries(currentReactions).forEach(([e, users]) => {
      const filtered = (users || []).filter(id => id !== myId);
      if (filtered.length > 0) {
        updatedReactions[e] = filtered;
      }
    });

    // Toggle logic: if user didn't have this emoji, add reaction
    if (!hadThisEmoji) {
      updatedReactions[emoji] = [...(updatedReactions[emoji] || []), myId];
    }

    // Optimistic UI update
    setMessages(prev => prev.map(m => (m.id === msg.id ? { ...m, reactions: updatedReactions } : m)));
    handleCloseReactionPicker();

    // Broadcast realtime event and update DB
    await chatService.sendReaction(broadcastChannelRef.current, msg.id, updatedReactions, currentUser.userid);
  };

  const handleIncomingReaction = (payload) => {
    if (!payload || !payload.messageId) return;
    setMessages(prev => prev.map(m => (m.id === payload.messageId ? { ...m, reactions: payload.reactions } : m)));
  };

  useEffect(() => {
    loadMessages();
    
    // Subscribe to new messages specifically for this conversation
    const channel = chatService.subscribeToMessages(currentUser.userid, (msg) => {
      if (msg.sender_id === recipient.userid) {
        setMessages(prev => [...prev, msg]);
        chatService.markAsRead([msg.id]);
        if (onRead) onRead();
        scrollToBottom();
      }
    });
    subscriptionRef.current = channel;

    // Subscribe to Typing and Reaction indicators
    const typingChannel = chatService.joinConversation(
      currentUser.userid, 
      recipient.userid, 
      () => {
        setIsTyping(true);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 3000);
      },
      handleIncomingReaction
    );
    broadcastChannelRef.current = typingChannel;

    return () => {
      if (subscriptionRef.current) supabase.removeChannel(subscriptionRef.current);
      if (broadcastChannelRef.current) supabase.removeChannel(broadcastChannelRef.current);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [recipient.userid, currentUser.userid, onRead]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    const now = Date.now();
    if (now - lastTypingSentRef.current > 2000) {
      chatService.sendTyping(broadcastChannelRef.current, currentUser.userid);
      lastTypingSentRef.current = now;
    }
  };

  const loadMessages = async () => {
    setLoading(true);
    const res = await chatService.getMessages(currentUser.userid, recipient.userid);
    if (res.success) {
      setMessages(res.data);
      const unreadIds = res.data
        .filter(m => !m.is_read && m.receiver_id === currentUser.userid)
        .map(m => m.id);
      if (unreadIds.length > 0) {
        chatService.markAsRead(unreadIds);
        if (onRead) onRead();
      }
    }
    setLoading(false);
  };

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;

    const content = newMessage.trim();
    setNewMessage('');
    setSending(true);

    // Optimistic UI update
    const tempMsg = {
      id: `temp-${Date.now()}`,
      sender_id: currentUser.userid,
      receiver_id: recipient.userid,
      content: content,
      created_at: new Date().toISOString(),
      is_read: false,
      reactions: {}
    };
    setMessages(prev => [...prev, tempMsg]);
    scrollToBottom();

    const res = await chatService.sendMessage(currentUser.userid, recipient.userid, content);
    if (res.success) {
      setMessages(prev => prev.map(m => m.id === tempMsg.id ? res.data : m));
    } else {
      setMessages(prev => prev.filter(m => m.id !== tempMsg.id));
      alert('Failed to send message: ' + res.error);
    }
    setSending(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const triggerClose = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (onClose) onClose();
  };

  const triggerMinimize = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (onMinimize) onMinimize();
  };

  return (
    <Paper 
      elevation={12}
      sx={{
        position: 'fixed',
        bottom: { xs: 74, md: 0 },
        right: { xs: 12, sm: position * 340 + 360 },
        width: { xs: 'calc(100vw - 24px)', sm: 320 },
        maxWidth: 340,
        height: 430,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px 16px 0 0',
        overflow: 'hidden',
        border: '1px solid #e2e8f0',
        zIndex: 2147483000,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15)'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 1.5, 
        bgcolor: '#059669', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'default'
      }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Badge 
            variant="dot" 
            color={recipient.isOnline ? 'success' : 'default'}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            sx={{
              '& .MuiBadge-badge': {
                bgcolor: recipient.isOnline ? '#10b981' : '#cbd5e1',
                border: '2px solid white'
              }
            }}
          >
            <Avatar sx={{ width: 30, height: 30, fontSize: '0.75rem', bgcolor: 'rgba(255, 255, 255, 0.25)', color: '#ffffff', fontWeight: 700 }}>
              {recipient.firstname?.[0]}{recipient.lastname?.[0]}
            </Avatar>
          </Badge>
          <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ maxWidth: 160, color: '#ffffff' }}>
            {recipient.firstname} {recipient.lastname}
          </Typography>
          {unread > 0 && (
            <Badge badgeContent={unread} color="error" sx={{ ml: 1 }} />
          )}
        </Box>
        <Box>
          <IconButton
            size="small"
            onMouseDown={triggerMinimize}
            onTouchStart={triggerMinimize}
            onClick={triggerMinimize}
            sx={{ color: 'white' }}
          >
            <Minimize fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onMouseDown={triggerClose}
            onTouchStart={triggerClose}
            onClick={triggerClose}
            sx={{ color: 'white' }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Messages Area */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto', 
        p: 1.75,
        bgcolor: '#f8faf9',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25
      }}>
        {loading ? (
          <Box display="flex" justifyContent="center" pt={5}>
            <CircularProgress size={24} sx={{ color: '#059669' }} />
          </Box>
        ) : (
          messages.map((msg, index) => {
            const isMe = msg.sender_id === currentUser.userid;
            const reactionsObj = msg.reactions || {};
            const hasReactions = Object.keys(reactionsObj).length > 0;

            return (
              <Box 
                key={msg.id || index}
                sx={{
                  alignSelf: isMe ? 'flex-end' : 'flex-start',
                  maxWidth: '84%',
                  position: 'relative',
                  '&:hover .reaction-trigger': { opacity: 1 }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexDirection: isMe ? 'row-reverse' : 'row' }}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 1.25, 
                      bgcolor: isMe ? '#059669' : '#ffffff',
                      color: isMe ? '#ffffff' : '#0f172a',
                      borderRadius: isMe ? '14px 14px 2px 14px' : '14px 14px 14px 2px',
                      border: isMe ? 'none' : '1px solid #e2e8f0',
                      boxShadow: isMe ? '0 2px 4px rgba(5, 150, 105, 0.2)' : '0 1px 2px rgba(0,0,0,0.04)'
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '0.84rem', lineHeight: 1.45, wordBreak: 'break-word' }}>{msg.content}</Typography>
                  </Paper>

                  {/* Reaction Picker Button */}
                  <Tooltip title="Add Reaction" arrow placement="top">
                    <IconButton
                      size="small"
                      className="reaction-trigger"
                      onClick={(e) => handleOpenReactionPicker(e, msg)}
                      sx={{
                        opacity: { xs: 0.8, sm: 0 },
                        transition: 'opacity 0.15s ease',
                        p: 0.5,
                        color: '#64748b',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.05)', color: '#059669' }
                      }}
                    >
                      <AddReactionOutlined sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Reaction Badges */}
                {hasReactions && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    sx={{
                      mt: 0.5,
                      justifyContent: isMe ? 'flex-end' : 'flex-start'
                    }}
                  >
                    {Object.entries(reactionsObj).map(([emoji, users]) => {
                      if (!users || users.length === 0) return null;
                      const hasReacted = users.includes(String(currentUser.userid));
                      return (
                        <Box
                          key={emoji}
                          onClick={() => handleToggleReaction(msg, emoji)}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.3,
                            px: 0.75,
                            py: 0.15,
                            borderRadius: '10px',
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            bgcolor: hasReacted ? '#ecfdf5' : '#ffffff',
                            border: hasReacted ? '1px solid #10b981' : '1px solid #cbd5e1',
                            color: hasReacted ? '#047857' : '#334155',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              transform: 'scale(1.06)',
                              bgcolor: '#f1f5f3'
                            }
                          }}
                        >
                          <span>{emoji}</span>
                          <span style={{ fontSize: '0.68rem' }}>{users.length}</span>
                        </Box>
                      );
                    })}
                  </Stack>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: isMe ? 'right' : 'left', mt: 0.35, fontSize: '0.68rem', color: '#94a3b8' }}>
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>
            );
          })
        )}
        {isTyping && (
          <Box sx={{ alignSelf: 'flex-start', mb: 1, ml: 1 }}>
             <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', color: '#059669', fontWeight: 600 }}>
               Typing...
             </Typography>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box sx={{ p: 1.25, bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
        <Box display="flex" gap={1} alignItems="center">
          <TextField
            fullWidth
            size="small"
            placeholder="Type a message..."
            value={newMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            multiline
            maxRows={3}
            sx={{ 
              '& .MuiOutlinedInput-root': { 
                borderRadius: 3,
                fontSize: '0.8125rem'
              } 
            }}
          />
          <IconButton 
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            sx={{
              bgcolor: '#059669',
              color: '#ffffff',
              '&:hover': { bgcolor: '#047857' },
              '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' },
              width: 36,
              height: 36
            }}
          >
            <Send sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Quick Emoji Reaction Picker Popover */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handleCloseReactionPicker}
        container={typeof document !== 'undefined' ? document.body : undefined}
        sx={{ zIndex: 2147483647 }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            p: 0.75,
            borderRadius: '24px',
            boxShadow: '0 12px 28px rgba(0, 0, 0, 0.25)',
            border: '1px solid #cbd5e1',
            bgcolor: '#ffffff',
            zIndex: 2147483647
          }
        }}
      >
        <Stack direction="row" spacing={0.5}>
          {QUICK_EMOJIS.map(emoji => (
            <IconButton
              key={emoji}
              size="small"
              onClick={() => activeMsgForReaction && handleToggleReaction(activeMsgForReaction, emoji)}
              sx={{
                fontSize: '1.15rem',
                p: 0.5,
                borderRadius: '50%',
                transition: 'transform 0.15s ease',
                '&:hover': {
                  transform: 'scale(1.3)',
                  bgcolor: '#ecfdf5'
                }
              }}
            >
              {emoji}
            </IconButton>
          ))}
        </Stack>
      </Popover>
    </Paper>
  );
};

export default ChatWindow;
